/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text
*/
<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const { currentAccount } = useAccount();
const { t } = useI18n();
const data = ref({
  questions: [],
  answers: {},
  sessions_remaining: 4,
  applied: false,
});
const text = ref('');
const busy = ref(false);
const url = computed(
  () =>
    '/api/v1/accounts/' + currentAccount.value.id + '/lumerai/knowledge_builder'
);
const questionKeys = [
  'company',
  'audience',
  'offerings',
  'pricing',
  'availability',
  'locations',
  'workflow',
  'data',
  'limits',
  'tone',
  'handoff',
  'integrations',
  'faq',
  'examples',
];
const localizedQuestionLabels = computed(() =>
  Object.fromEntries(
    questionKeys.map(key => [
      key,
      {
        // key is drawn from the fixed questionKeys list above.
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        label: t(`LUMERAI_SETTINGS.BUILDER.QUESTIONS.${key}.LABEL`),
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        hint: t(`LUMERAI_SETTINGS.BUILDER.QUESTIONS.${key}.HINT`),
      },
    ])
  )
);
const q = computed(() =>
  data.value.questions.find(key => !data.value.answers[key])
);
const currentQuestion = computed(() =>
  q.value
    ? localizedQuestionLabels.value[q.value] || { label: q.value, hint: '' }
    : null
);
const answered = computed(() =>
  data.value.questions.filter(key => data.value.answers[key])
);
async function load() {
  try {
    data.value = (await window.axios.get(url.value)).data;
  } catch (e) {
    useAlert(t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_BUILDER_LOAD_ERROR'));
  }
}
async function answer() {
  if (!q.value || !text.value.trim()) {
    useAlert(t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_ANSWER_REQUIRED'));
    return;
  }
  busy.value = true;
  try {
    data.value = (
      await window.axios.post(url.value, { answers: { [q.value]: text.value } })
    ).data;
    text.value = '';
  } catch (e) {
    useAlert(t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_ANSWER_SAVE_ERROR'));
  } finally {
    busy.value = false;
  }
}
async function resetBuilder() {
  const warning = data.value.applied
    ? t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_BUILDER_RESET_WITH_ACTIVE_BASE')
    : t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_BUILDER_RESET_CONFIRM');
  // Native confirmation is required because the interview responses are deleted.
  // eslint-disable-next-line no-alert
  if (!window.confirm(warning)) return;
  busy.value = true;
  try {
    data.value = (await window.axios.delete(url.value)).data;
    text.value = '';
    useAlert(t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_BUILDER_RESET'));
  } catch (e) {
    useAlert(t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_BUILDER_RESET_ERROR'));
  } finally {
    busy.value = false;
  }
}
async function apply() {
  busy.value = true;
  try {
    data.value = (await window.axios.patch(url.value, {})).data;
    // Keep the parent settings screen in sync when the builder is completed.
    // The playground sends these values as preview attributes, so stale refs
    // would otherwise hide the newly approved knowledge until a reload.
    window.dispatchEvent(
      new CustomEvent('lumerai-knowledge-updated', {
        detail: {
          ...(data.value.structured || {}),
          ai_knowledge_base: data.value.draft || '',
        },
      })
    );
    useAlert(t('LUMERAI_SETTINGS.ALERTS.KNOWLEDGE_BASE_APPLIED'));
  } catch (e) {
    useAlert(
      e.response?.data?.error || 'Complete a entrevista antes de aplicar.'
    );
  } finally {
    busy.value = false;
  }
}
onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <section
    class="flex flex-col overflow-hidden rounded-xl border border-n-strong bg-white dark:bg-n-solid-2"
  >
    <header
      class="flex items-center justify-between border-b border-n-strong px-5 py-4"
    >
      <div>
        <h3 class="text-lg font-semibold">
          {{ t('LUMERAI_SETTINGS.BUILDER.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('LUMERAI_SETTINGS.BUILDER.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('LUMERAI_SETTINGS.BUILDER.RESET')"
          color="slate"
          sm
          :is-loading="busy"
          @click="resetBuilder"
        />
        <span
          class="rounded-full px-3 py-1 text-xs"
          :class="
            data.applied
              ? 'bg-n-success-1 text-n-success-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          {{
            data.applied
              ? t('LUMERAI_SETTINGS.BUILDER.APPLIED')
              : t('LUMERAI_SETTINGS.BUILDER.USES', {
                  count: data.sessions_remaining,
                })
          }}
        </span>
      </div>
    </header>
    <div class="flex max-h-[560px] flex-col gap-4 overflow-y-auto p-5">
      <div
        class="max-w-[85%] rounded-2xl rounded-tl-sm bg-n-alpha-2 px-4 py-3 text-sm"
      >
        {{ t('LUMERAI_SETTINGS.BUILDER.GREETING') }}
      </div>
      <template v-for="key in answered" :key="key">
        <div
          class="max-w-[85%] rounded-2xl rounded-tl-sm bg-n-alpha-2 px-4 py-3 text-sm"
        >
          <p class="font-medium">
            {{ localizedQuestionLabels[key]?.label || key }}
          </p>
          <p class="mt-2 whitespace-pre-wrap text-n-slate-11">
            {{ data.answers[key] }}
          </p>
        </div>
      </template>
      <div
        v-if="currentQuestion"
        class="max-w-[90%] rounded-2xl rounded-tl-sm bg-n-brand/10 px-4 py-3 text-sm"
      >
        <p class="font-medium">{{ currentQuestion.label }}</p>
        <p class="mt-1 text-n-slate-11">{{ currentQuestion.hint }}</p>
      </div>
      <div
        v-else
        class="max-w-[90%] rounded-2xl rounded-tl-sm bg-n-brand/10 px-4 py-3 text-sm"
      >
        <template v-if="data.applied">
          {{ t('LUMERAI_SETTINGS.BUILDER.APPLY_SUCCESS') }}
          <div v-if="data.structured" class="mt-3 grid gap-2 text-xs">
            <p class="font-semibold">
              {{ t('LUMERAI_SETTINGS.BUILDER.STRUCTURED_FIELDS') }}
            </p>
            <p
              v-for="(value, key) in data.structured"
              :key="key"
              class="rounded bg-n-alpha-2 px-2 py-1"
            >
              <span class="font-medium">{{ key }}:</span>
              <span class="whitespace-pre-wrap">
                {{ Array.isArray(value) ? value.length + ' item(ns)' : value }}
              </span>
            </p>
          </div>
        </template>
        <template v-else>
          {{ t('LUMERAI_SETTINGS.BUILDER.COMPLETE') }}
        </template>
      </div>
    </div>
    <footer class="border-t border-n-strong p-4">
      <div v-if="currentQuestion" class="flex items-end gap-3">
        <TextArea
          v-model="text"
          rows="3"
          class="flex-1"
          :placeholder="t('LUMERAI_SETTINGS.BUILDER.ANSWER_PLACEHOLDER')"
          @keydown.enter.exact.prevent="answer"
        /><Button
          :label="t('LUMERAI_SETTINGS.BUILDER.SEND')"
          color="blue"
          :is-loading="busy"
          @click="answer"
        />
      </div>
      <Button
        v-else-if="!data.applied"
        :label="t('LUMERAI_SETTINGS.BUILDER.APPLY')"
        color="blue"
        :is-loading="busy"
        @click="apply"
      />
    </footer>
  </section>
</template>
